// ─────────────────────────────────────────────────────────────
// DerivedTagChips — renders the auto-derived search tags
// (modality / magnification / signal / instrument / calibrated)
// that the backend computes from FEI acquisition metadata.
//
// Visual contract: derived chips are *outlined* (system-owned,
// read-only) so they read as visually distinct from filled
// user-entered tag chips elsewhere in the card. Null fields
// render nothing — the keys are always present on the API
// payload but the values are nullable per Sam's contract
// (PROGRESS.md 2026-05-05). `calibrated: false` is silent;
// only `calibrated: true` shows a chip (positive affordance,
// not noise).
//
// Compact card variant (2026-05-05 follow-up): renders Modality,
// Magnification, Instrument, and Calibrated. Signal moves to the
// non-compact variant / detail dialog. Calibrated becomes
// icon-only (green check) — tooltip carries the meaning.
// Magnification gets a rich tooltip pulling HFW + pixel size
// straight from `acquisition_metadata`.
// ─────────────────────────────────────────────────────────────

import { Box, Chip, Tooltip } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import type { AcquisitionMetadata, DerivedTags } from "../../api/client";

interface DerivedTagChipsProps {
  derived?: DerivedTags | null;
  /**
   * Acquisition metadata — used to build the rich Magnification tooltip
   * (HFW + pixel size). Optional; if absent we fall back to the bucket
   * label only.
   */
  metadata?: AcquisitionMetadata | null;
  /**
   * Compact mode — used on the dense card surface. Renders modality,
   * magnification, instrument, and calibrated. Signal stays in the
   * non-compact variant / detail dialog so the card doesn't crowd.
   */
  compact?: boolean;
}

export function DerivedTagChips({
  derived,
  metadata,
  compact = false,
}: DerivedTagChipsProps) {
  if (!derived) return null;
  const { modality, magnification, signal, calibrated, instrument } = derived;

  // Hide the entire row only when nothing would render. `calibrated: false`
  // is silent, so we explicitly check for `=== true` here.
  const anyChip =
    modality || magnification || signal || instrument || calibrated === true;
  if (!anyChip) return null;

  // Signal stays out of the compact card variant — it's already represented
  // (loosely) by modality, and we'd rather keep the card to four chips.
  const showSignalInline = !compact;
  // Instrument is now ON the card per Greg's 2026-05-05 follow-up.
  const showInstrumentInline = true;

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 0.375,
        alignItems: "center",
      }}
    >
      {modality && (
        <Tooltip
          title={
            compact
              ? buildModalityTooltip(modality, signal)
              : `Modality: ${modality}`
          }
          placement="top"
        >
          <Chip
            label={modality}
            size="small"
            variant="filled"
            sx={modalityChipSx(modalityColor(modality))}
          />
        </Tooltip>
      )}
      {magnification && (
        <Tooltip
          title={buildMagnificationTooltip(magnification, metadata)}
          placement="top"
        >
          <Chip
            label={magnification.replace("-mag", "")}
            size="small"
            variant="outlined"
            sx={chipSx("default")}
          />
        </Tooltip>
      )}
      {showSignalInline && signal && (
        <Tooltip title={`Signal: ${signal}`} placement="top">
          <Chip
            label={signal}
            size="small"
            variant="outlined"
            sx={chipSx("default")}
          />
        </Tooltip>
      )}
      {showInstrumentInline && instrument && (
        <Tooltip title={`Instrument: ${instrument}`} placement="top">
          <Chip
            label={instrument}
            size="small"
            variant="outlined"
            sx={chipSx("default")}
          />
        </Tooltip>
      )}
      {calibrated === true && (
        <Tooltip
          title="Calibrated — pixel size known. Measurements will be in real-world units."
          placement="top"
        >
          {/* Icon-only chip: tooltip carries the label. */}
          <Chip
            label=""
            size="small"
            variant="outlined"
            icon={<CheckCircleOutlineIcon sx={{ fontSize: 13 }} />}
            sx={iconOnlyChipSx("calibrated")}
            aria-label="Calibrated"
          />
        </Tooltip>
      )}
    </Box>
  );
}

function buildModalityTooltip(
  modality: string,
  signal: string | null,
): string {
  const parts = [`Modality: ${modality}`];
  if (signal) parts.push(`Signal: ${signal}`);
  return parts.join(" • ");
}

/**
 * Magnification tooltip:
 *   "<bucket> · HFW <hfw_um> µm · <pixel_size_nm> nm/px"
 * Each segment after the bucket only renders when the underlying value
 * is present. Falls back to just the bucket label when neither HFW nor
 * pixel size is available.
 */
function buildMagnificationTooltip(
  bucket: string,
  metadata: AcquisitionMetadata | null | undefined,
): string {
  const parts: string[] = [bucket];
  if (metadata) {
    const hfw = metadata.hfw_um;
    if (typeof hfw === "number" && Number.isFinite(hfw)) {
      parts.push(`HFW ${formatHfw(hfw)} µm`);
    }
    const px = metadata.pixel_size_nm;
    if (typeof px === "number" && Number.isFinite(px)) {
      parts.push(`${formatPixelSize(px)} nm/px`);
    }
  }
  // If we only have the bucket, prefix with "Magnification:" so the
  // tooltip still reads as a label rather than a bare word.
  if (parts.length === 1) return `Magnification: ${bucket}`;
  return parts.join(" · ");
}

function formatHfw(hfw_um: number): string {
  // Decade-aware formatting: large HFW shows whole µm, small HFW shows
  // a couple of decimals so the user can actually read the value.
  if (hfw_um >= 100) return hfw_um.toFixed(0);
  if (hfw_um >= 10) return hfw_um.toFixed(1);
  return hfw_um.toFixed(2);
}

function formatPixelSize(px_nm: number): string {
  if (px_nm >= 100) return px_nm.toFixed(0);
  if (px_nm >= 10) return px_nm.toFixed(1);
  return px_nm.toFixed(2);
}

// ── Color tokens ──────────────────────────────────────────────
//
// Two visual treatments live here:
//
// 1. Modality chips (SEM / TEM / SDB / Optical) — *filled* with a
//    darker, saturated brand-aligned color and white text. The pastel
//    cyan/amber/teal/green outlined treatment that shipped 2026-05-05
//    failed contrast on the white card surface (Greg's eyeball check),
//    so they're now solid color blocks. Each modality fill clears
//    ≥4.5:1 contrast against `#ffffff` text per WCAG. On dark theme
//    card surfaces the same saturated fills sit cleanly above
//    `background.paper` (~#1e1e1e) — the high chroma keeps them legible.
//
// 2. Everything else (Magnification / Instrument / Calibrated / etc.)
//    stays *outlined* — those read fine on both surfaces and the
//    outlined treatment keeps them visually subordinate to the filled
//    modality chip and to user-entered tag chips elsewhere on the card.

type ChipColorToken = "default" | "calibrated" | "sem" | "tem" | "sdb" | "optical";

function modalityColor(modality: string): ChipColorToken {
  const m = modality.toUpperCase();
  if (m === "SEM") return "sem";
  if (m === "TEM") return "tem";
  if (m === "SDB") return "sdb";
  if (m === "OPTICAL") return "optical";
  return "default";
}

// Outlined-chip tones (default / calibrated / unused legacy modality fallbacks).
const CHIP_PALETTE: Record<ChipColorToken, { color: string; border: string }> = {
  default: { color: "#9e9e9e", border: "rgba(158,158,158,0.55)" },
  calibrated: { color: "#66bb6a", border: "rgba(102,187,106,0.6)" },
  sem: { color: "#4fc3f7", border: "rgba(79,195,247,0.6)" },
  tem: { color: "#ffb74d", border: "rgba(255,183,77,0.6)" },
  sdb: { color: "#80cbc4", border: "rgba(128,203,196,0.6)" },
  optical: { color: "#a5d6a7", border: "rgba(165,214,167,0.6)" },
};

// Filled modality fills — each clears ≥4.5:1 contrast against white text.
// SEM     #0277bd (Light Blue 800) → ~7.0:1 on white text
// TEM     #e65100 (Deep Orange 900) → ~5.0:1 on white text
// SDB     #00695c (Teal 800)        → ~7.4:1 on white text
// Optical #2e7d32 (Green 800)       → ~5.4:1 on white text
const MODALITY_FILL: Record<"sem" | "tem" | "sdb" | "optical", string> = {
  sem: "#0277bd",
  tem: "#e65100",
  sdb: "#00695c",
  optical: "#2e7d32",
};

function chipSx(token: ChipColorToken) {
  const palette = CHIP_PALETTE[token];
  return {
    height: 18,
    fontSize: "0.5625rem",
    fontWeight: 600,
    letterSpacing: 0.2,
    color: palette.color,
    borderColor: palette.border,
    bgcolor: "transparent",
    flexShrink: 0,
    "& .MuiChip-icon": {
      color: palette.color,
      ml: 0.375,
      mr: -0.125,
    },
    "& .MuiChip-label": { px: 0.75 },
  };
}

/**
 * Filled modality chip — solid background, white label, no border.
 * Falls back to the outlined treatment if a non-modality token is
 * passed (defensive — only the four modality tokens should ever
 * reach this code path today).
 */
function modalityChipSx(token: ChipColorToken) {
  const fill = (MODALITY_FILL as Record<string, string | undefined>)[token];
  if (!fill) return chipSx(token);
  return {
    height: 18,
    fontSize: "0.5625rem",
    fontWeight: 700,
    letterSpacing: 0.3,
    color: "#ffffff",
    bgcolor: fill,
    border: "none",
    flexShrink: 0,
    "& .MuiChip-label": { px: 0.75 },
  };
}

/**
 * Compact icon-only chip — used by the Calibrated indicator. The label
 * slot is intentionally empty (the tooltip carries the meaning); we
 * collapse the label padding to zero so the chip is just a small round
 * icon affordance with the outline color of the chip palette.
 */
function iconOnlyChipSx(token: ChipColorToken) {
  const palette = CHIP_PALETTE[token];
  return {
    height: 18,
    width: 18,
    minWidth: 18,
    color: palette.color,
    borderColor: palette.border,
    bgcolor: "transparent",
    flexShrink: 0,
    "& .MuiChip-icon": {
      color: palette.color,
      m: 0,
      fontSize: 13,
    },
    "& .MuiChip-label": { display: "none" },
  };
}

